// ============================================================
// ClientHistoryTab.js
// History tab inside <ClientDetailPage>. Lists AuditLog rows
// scoped to one Client (rows with that ClientID; NULL-ClientID
// global rows are excluded server-side).
//
// Backend reference:
//   backend/func-carehub-api/src/functions/admin-clients-history.js
//
// Filter bar: action + entityType (both server-validated enums).
// Selecting a filter resets the page to 1.
//
// Each row's "View" link opens <JsonDialog> showing the
// OldValues / NewValues columns from the audit row as two
// labelled JSON blocks. Sections with null values are skipped
// inside the dialog so 'created' actions (no OldValues) render
// cleanly.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DataTable from '../components/DataTable';
import Badge from '../components/Badge';
import JsonDialog from '../components/JsonDialog';
import { getAdminClientHistory } from '../api/adminClients';
import { translateError } from '../utils/errorMessages';
import { formatDateTime } from '../utils/format';
import './ClientHistoryTab.css';

const DEFAULT_PAGE_SIZE = 50;

const ACTION_OPTIONS = [
  { value: '',            label: 'All actions' },
  { value: 'created',     label: 'Created' },
  { value: 'updated',     label: 'Updated' },
  { value: 'deactivated', label: 'Deactivated' },
  { value: 'reactivated', label: 'Reactivated' },
  { value: 'deleted',     label: 'Deleted' },
  { value: 'revoked',     label: 'Revoked' },
  { value: 'redeemed',    label: 'Redeemed' },
  { value: 'granted',     label: 'Granted' },
  { value: 'expired',     label: 'Expired' },
];

const ENTITY_TYPE_OPTIONS = [
  { value: '',                  label: 'All entities' },
  { value: 'Tenants',           label: 'Tenants' },
  { value: 'Clients',           label: 'Clients' },
  { value: 'ClientUsers',       label: 'Users' },
  { value: 'ClientUserAgents',  label: 'User entitlements' },
  { value: 'Modules',           label: 'Modules' },
  { value: 'ClientModules',     label: 'Client modules' },
  { value: 'Invitations',       label: 'Invitations' },
];

// Badge variant by action. Maps to the existing Badge palette so
// colour semantics stay aligned with status pills elsewhere.
const ACTION_VARIANT = {
  created:     'success',
  updated:     'blue',
  deactivated: 'orange',
  reactivated: 'teal',
  deleted:     'danger',
  revoked:     'danger',
  redeemed:    'success',
  granted:     'teal',
  expired:     'neutral',
};

function parsePageParam(raw, fallback) {
  if (raw == null) return fallback;
  const n = parseInt(raw, 10);
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

function capitaliseAction(action) {
  if (!action) return '';
  return action.charAt(0).toUpperCase() + action.slice(1);
}

export default function ClientHistoryTab({ clientId }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const page       = parsePageParam(searchParams.get('page'), 1);
  const pageSize   = parsePageParam(searchParams.get('pageSize'), DEFAULT_PAGE_SIZE);
  const action     = searchParams.get('action') || '';
  const entityType = searchParams.get('entityType') || '';

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [detailRow, setDetailRow] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, pageSize };
      if (action)     params.action = action;
      if (entityType) params.entityType = entityType;
      const res = await getAdminClientHistory(clientId, params);
      setItems(res.items || []);
      setTotalCount(res.totalCount || 0);
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [clientId, page, pageSize, action, entityType]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleFilterChange(key, value) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value); else next.delete(key);
      next.delete('page');
      return next;
    });
  }

  function handlePageChange(nextPage) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextPage === 1) next.delete('page'); else next.set('page', String(nextPage));
      return next;
    });
  }

  const columns = [
    {
      key: 'changedAt',
      label: 'When',
      render: (row) => (
        <span className="client-history__muted">{formatDateTime(row.changedAt)}</span>
      ),
      width: '210px',
    },
    {
      key: 'changedBy',
      label: 'Who',
      render: (row) => (
        <span className="client-history__who">{row.changedBy || '—'}</span>
      ),
    },
    {
      key: 'entity',
      label: 'What',
      render: (row) => (
        <span className="client-history__entity">
          {row.entityType}
          {row.entityId != null && (
            <span className="client-history__entity-id"> #{row.entityId}</span>
          )}
        </span>
      ),
      width: '200px',
    },
    {
      key: 'action',
      label: 'Action',
      render: (row) => (
        <Badge variant={ACTION_VARIANT[row.action] || 'neutral'}>
          {capitaliseAction(row.action)}
        </Badge>
      ),
      width: '120px',
    },
    {
      key: 'details',
      label: 'Details',
      render: (row) => (
        <button
          type="button"
          className="client-history__view"
          onClick={() => setDetailRow(row)}
        >
          View
        </button>
      ),
      width: '90px',
    },
  ];

  const isFiltered = Boolean(action || entityType);
  const emptyMessage = isFiltered
    ? 'No audit rows match these filters.'
    : 'No audit history for this Client yet.';

  const dialogTitle = detailRow
    ? `${capitaliseAction(detailRow.action)} on ${detailRow.entityType}${
        detailRow.entityId != null ? ` #${detailRow.entityId}` : ''
      }`
    : '';

  return (
    <div className="client-history">
      <div className="client-history__toolbar">
        <label className="client-history__field">
          <span className="client-history__label">Action</span>
          <select
            value={action}
            onChange={(e) => handleFilterChange('action', e.target.value)}
            className="client-history__select"
          >
            {ACTION_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
        <label className="client-history__field">
          <span className="client-history__label">Entity</span>
          <select
            value={entityType}
            onChange={(e) => handleFilterChange('entityType', e.target.value)}
            className="client-history__select"
          >
            {ENTITY_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>
      </div>

      <DataTable
        data={items}
        columns={columns}
        rowKey={(row) => row.auditLogId}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        loading={loading}
        error={error}
        onRetry={fetchData}
        emptyMessage={emptyMessage}
      />

      <JsonDialog
        isOpen={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        title={dialogTitle}
        sections={detailRow ? [
          { label: 'Old values', value: detailRow.oldValues },
          { label: 'New values', value: detailRow.newValues },
          { label: 'Details',    value: detailRow.details },
        ] : []}
      />
    </div>
  );
}
